/**
 * BrokerageModal — Add / Edit a brokerage firm.
 *
 * One modal serves both cases: pass a `brokerage` prop to edit, omit it to
 * add. On save it creates or updates the firm and, if a MoU file was
 * chosen, uploads that document too (a new firm is created first so the
 * upload has an id to attach to).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, FileText, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { createBrokerage, updateBrokerage, uploadMou } from '../api/brokerages';
import { reraExpiryDays } from './BrokerageCard';

const TIERS = ['Top', 'Mid', 'Low'];
const STATUSES = ['Active', 'Inactive'];

const emptyForm = {
  name: '',
  contact_person: '',
  phone: '',
  email: '',
  rera_license: '',
  trade_license: '',
  rera_expiry: '',
  tier: 'Mid',
  notes: '',
  status: 'Active',
  // Ownership fields per the 05 Jun 2026 MoM. Free-text names —
  // we'll wire to a user picker once Shabana shares the BDM/Sales
  // Manager roster.
  bdm_name: '',
  sales_manager_name: '',
};

// A backend ISO date may carry a time component — trim to yyyy-mm-dd for
// the native date input.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export default function BrokerageModal({ open, onOpenChange, brokerage, onSaved }) {
  const { token } = useAuth();
  const isEdit = Boolean(brokerage?.id);
  const [form, setForm] = useState(emptyForm);
  const [mouFile, setMouFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Re-seed the form every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setMouFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setForm(
      brokerage
        ? {
            name: brokerage.name || '',
            contact_person: brokerage.contact_person || '',
            phone: brokerage.phone || '',
            email: brokerage.email || '',
            rera_license: brokerage.rera_license || brokerage.rera_number || '',
            trade_license: brokerage.trade_license || '',
            rera_expiry: toDateInput(brokerage.rera_expiry),
            tier: brokerage.tier || 'Mid',
            notes: brokerage.notes || '',
            status: brokerage.status || 'Active',
            bdm_name: brokerage.bdm_name || '',
            sales_manager_name: brokerage.sales_manager_name || '',
          }
        : emptyForm,
    );
  }, [open, brokerage]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const expiryDays = reraExpiryDays(form.rera_expiry);
  const expirySoon = expiryDays !== null && expiryDays <= 60;

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Brokerage name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      const saved = isEdit
        ? await updateBrokerage(token, brokerage.id, payload)
        : await createBrokerage(token, payload);

      // Attach the MoU document if one was picked. The firm now exists
      // either way, so we always have an id to upload against.
      if (mouFile) {
        try {
          await uploadMou(token, saved.id, mouFile);
        } catch (e) {
          toast.warning('Brokerage saved, but the MoU upload failed — retry from the detail screen.');
        }
      }

      toast.success(isEdit ? 'Brokerage updated' : 'Brokerage added');
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      // The backend now returns a structured 409 body when any of
      // the canonical brokerage identifiers (name / RERA / trade
      // license / email / phone) collides with an existing firm.
      // Render that as an actionable toast instead of "[object
      // Object]" or a generic save-failed.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object') {
        toast.error(
          detail.message
            || `A brokerage with this ${detail.conflict_field || 'detail'} already exists`,
          {
            description: detail.existing_name
              ? `Conflict with existing firm: ${detail.existing_name}`
              : undefined,
          },
        );
      } else {
        toast.error(detail || e?.message || 'Could not save the brokerage');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Brokerage' : 'Add Brokerage'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this brokerage partner firm.'
              : 'Register a new external brokerage partner firm.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <Field label="Brokerage name" required>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Betterhomes"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact person">
              <Input
                value={form.contact_person}
                onChange={(e) => set('contact_person', e.target.value)}
                placeholder="Full name"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+971…"
              />
            </Field>
          </div>

          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="partner@brokerage.com"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="RERA licence">
              <Input
                value={form.rera_license}
                onChange={(e) => set('rera_license', e.target.value)}
                placeholder="RERA-XXXXX"
              />
            </Field>
            <Field label="RERA expiry">
              <Input
                type="date"
                value={form.rera_expiry}
                onChange={(e) => set('rera_expiry', e.target.value)}
              />
            </Field>
          </div>
          {expirySoon && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              <AlertTriangle size={12} />
              {expiryDays < 0
                ? 'This RERA licence has already expired.'
                : `This RERA licence expires in ${expiryDays} day${expiryDays === 1 ? '' : 's'}.`}
            </div>
          )}

          {/* Trade license — second canonical identifier the backend
              dedup guard checks against. Capturing it here means a
              second firm with the same trade license gets 409'd. */}
          <Field label="Trade licence">
            <Input
              value={form.trade_license}
              onChange={(e) => set('trade_license', e.target.value)}
              placeholder="DED-XXXXXXX"
            />
          </Field>

          {/* Ownership — per MoM 05 Jun 2026 §4. BDM owns the
              brokerage relationship; Sales Manager owns the leads
              that come through it. */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Business Development Manager">
              <Input
                value={form.bdm_name}
                onChange={(e) => set('bdm_name', e.target.value)}
                placeholder="BDM owning this brokerage"
              />
            </Field>
            <Field label="Sales Manager">
              <Input
                value={form.sales_manager_name}
                onChange={(e) => set('sales_manager_name', e.target.value)}
                placeholder="Sales Manager for incoming leads"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tier">
              <Select value={form.tier} onValueChange={(v) => set('tier', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="MoU document (PDF)">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => setMouFile(e.target.files?.[0] || null)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={14} className="mr-1.5" />
                Choose PDF
              </Button>
              <span className="text-xs text-slate-500 truncate flex items-center gap-1">
                {mouFile ? (
                  <><FileText size={12} /> {mouFile.name}</>
                ) : brokerage?.mou_filename ? (
                  <><FileText size={12} /> {brokerage.mou_filename} (on file)</>
                ) : (
                  'No document selected'
                )}
              </span>
            </div>
          </Field>

          <Field label="Notes">
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Anything worth remembering about this partner…"
            />
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add brokerage'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);
